import { createFileRoute } from "@tanstack/react-router";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  { q: "Kan ni animera i vår befintliga grafiska profil?", a: "Ja. Vi utgår från er brand book och bygger ett rörelsespråk ur den — typografi, färg, timing och övergångar. Resultatet blir en mall ni känner igen er i och kan återanvända." },
  { q: "Kan vi få en mall vi uppdaterar själva?", a: "Ja. Vi levererar Essential Graphics-mallar i After Effects eller Lottie-filer där text, siffror och färg är redigerbara utan att animationen bryts." },
  { q: "Vad är motion graphics?", a: "Animation av grafiska element — text, ikoner, illustrationer, logotyper — i rörelse. Typiskt 2D, ofta i After Effects eller liknande. Bra för förklaringsfilm, SaaS-demos, social-content och titlar/transitions inom annan film." },
  { q: "Vad kostar motion graphics?", a: "Kort lower-third eller social-grafik: 5–15 kkr. Förklaringsfilm 60–90 sek: 40–120 kkr. Komplex datadriven motion graphics eller längre format: från 120 kkr." },
  { q: "Hur lång produktionstid?", a: "Korta klipp: 1–2 veckor. 60–90 sek förklaringsfilm: 4–6 veckor (manus → storyboard → animatic → animation → ljud)." },
  { q: "Får vi källfiler?", a: "Ja — på begäran och oftast inkluderat i offerten. Vi levererar i After Effects/Lottie så ni kan göra mindre uppdateringar själva utan att börja om." },
];

export const Route = createFileRoute("/motion-graphics")({
  head: () => ({
    meta: [
      { title: "Motion graphics — pris, process och exempel 2026 | Fenny" },
      { name: "description", content: "Vad kostar motion graphics? Se prisspann, process steg för steg och exempel på 2D-animation för förklaringsfilm och socialt innehåll. Fast pris, källfiler ingår." },
      { property: "og:title", content: "Motion graphics — Fenny" },
      { property: "og:description", content: "2D-animation som förklarar och hookar." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/motion-graphics" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/motion-graphics" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/motion-graphics", name: "Motion graphics — 2D-animation som funkar | Fenny", description: "Motion graphics för förklaringsfilm, social-content, lower-thirds och datadriven animation. Strategi, manus och animation från Fenny."})) },
      { type: "application/ld+json", children: JSON.stringify(serviceSchema({ name: "Motion graphics", path: "/motion-graphics", serviceType: "Motion graphics", description: "2D-animation för förklaringsfilm, social-content och datadriven motion graphics." })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Motion graphics", path: "/motion-graphics" }])) },
    ],
  }),
  component: () => (
    <>
      <LandingPage
      eyebrow="Motion graphics"
      title={<>Motion graphics där varje rörelse betyder något.</>}
      lede="Vi producerar motion graphics som förklarar, hookar och bygger varumärke — utan att bli generisk template. Från lower-thirds till 90-sekunders förklaringsfilm och datadriven animation."
      tldr={<p>Motion graphics är animerad grafik — logo-animationer, intros/outros, info-grafik, social-cards. Pris: <strong>5 000–40 000 kr</strong> per asset eller som del av större produktion. Fenny levererar motion graphics fristående eller integrerat i Pulse-abonnemang för löpande sociala assets.</p>}
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Animerad förklaring", to: "/animerad-forklaringsfilm" }}
      trackingId="motion-graphics"
      relatedLinksFor="/motion-graphics"
      trust={[
        { value: "5–40 kkr", label: "per fristående asset" },
        { value: "1–2 v", label: "för korta klipp" },
        { value: "Källfiler", label: "AE/Lottie ingår" },
      ]}
      pricing={{
        heading: "Vad motion graphics kostar",
        intro: "Riktpriser 2026 exkl. moms. Assets i paket blir betydligt billigare per styck eftersom stilguide och mall byggs en gång.",
        rows: [
          { format: "Enstaka asset", scope: "Logo-sting, lower-third, social-card eller intro/outro", price: "5 000–15 000 kr", lead: "1 vecka" },
          { format: "Grafikpaket", scope: "Stilguide plus 8–12 återanvändbara mallar för film och social", price: "35 000–90 000 kr", lead: "3 veckor" },
          { format: "Förklaringsfilm", scope: "60–90 sek animerad film med manus, voice-over och ljud", price: "40 000–120 000 kr", lead: "4–6 veckor" },
        ],
        footnote: "Datadriven motion (KPI-filmer, årsredovisning) offereras separat och startar kring 120 000 kr.",
      }}
      process={{
        heading: "Process",
        steps: [
          { label: "Manus & mood", body: "Budskap först, sedan referensbilder och rörelsereferenser.", duration: "Vecka 1" },
          { label: "Storyboard", body: "Nyckelbilder i vald stil — här godkänns utseendet.", duration: "Vecka 2" },
          { label: "Animatic", body: "Timing utan färdig animation. Billigt att ändra rytm och ordning.", duration: "Vecka 3" },
          { label: "Animation", body: "Full produktion, inklusive versioner för 16:9, 1:1 och 9:16.", duration: "Vecka 4–5" },
          { label: "Ljud & källfiler", body: "Sound design, mix, export och överlämning av redigerbara projektfiler.", duration: "Vecka 6" },
        ],
      }}
      objections={{
        heading: "Vanliga frågor innan beställning",
        items: [
          { objection: "Kan vi inte köpa en mall på nätet?", answer: <>Ni kan — och den ser ut som alla andras. Mall är rätt för intern kommunikation, custom är rätt när varumärket ska synas utåt.</> },
          { objection: "Vi behöver bara några få klipp i månaden.", answer: <>Då passar Pulse bättre än projekt. Löpande motion-assets ingår i abonnemanget utan offert per klipp.</> },
        ],
      }}
      bullets={[
        { title: "Strategi före stil", body: "Animationen ska bära budskapet — inte stjäla showen." },
        { title: "Källfiler ingår", body: "After Effects/Lottie med ren projektstruktur — ni kan uppdatera själva." },
        { title: "Skalar i kanaler", body: "16:9, 9:16, 1:1 med och utan textning — från start." },
      ]}
      sections={[
        { id: "anvandning", heading: "Vad motion graphics används till", body: <p>Förklaringsfilm för SaaS, fintech och B2B. Social-content (vertikala animationer). Lower-thirds och titlar i annan film. Datadriven motion (resultatfilmer, årsredovisning, KPI:er). Logo-stings för podcasts och video.</p> },
        { id: "process", heading: "Process", body: <p>Manus → moodboard → storyboard → animatic (timing-test utan färdig animation) → animation → ljud → leverans. Vi godkänner i etapper för att undvika dyra omtagningar.</p> },
        { id: "stil", heading: "Stilfrågan", body: <p>Mall-stilar (Lottie-pack, generisk corporate) är billiga men ser ut som alla andra. Custom-stil tar längre tid men gör att varumärket faktiskt syns. Vi rekommenderar custom på 80 % av jobben.</p> },
      ]}
      faq={FAQ}
      caseLimit={4}
      riskReducers
      leadMagnet={{
        slug: "brief-mall",
        title: "Ladda ner: brief-mallen som inte slösar tid",
        description: "1 sida, 7 fält som styr utfallet, plus ett ifyllt exempel. Använd den innan ni tar in offerter.",
      }}
      closing={{ title: "Något att animera?", body: "30 minuter där vi tittar på manus, stil och budget.", primary: { label: "Boka möte", to: "/kontakt" } }}
    />
      <RelatedKnowledgeAuto topics={["motion-graphics", "animation", "3d-animation", "explainer"]} />
    </>
  ),
});
